/**
 * Pure positioning math for the walkthrough tip card, split out so it can be
 * unit tested without a DOM. The one rule: the card must always stay fully
 * inside the viewport (top never above TOUR_GAP), so a tall card near the top
 * of the page can never be clipped off-screen. When the card is taller than
 * the viewport it pins to the top and the card element scrolls internally.
 */

export const TOUR_GAP = 12
export const TOUR_CARD_WIDTH = 360

export type TourRect = {
  readonly top: number
  readonly left: number
  readonly width: number
  readonly height: number
}

export type TourPosition = {
  readonly top: number
  readonly left: number
}

export function computeTourCardPosition(
  rect: TourRect | null,
  cardH: number,
  vw: number,
  vh: number,
): TourPosition {
  const G = TOUR_GAP
  const W = TOUR_CARD_WIDTH

  if (rect) {
    const below = rect.top + rect.height + G
    const above = rect.top - G - cardH
    let top: number
    if (below + cardH <= vh - G) top = below
    else if (above >= G) top = above
    else top = vh - G - cardH
    // Clamp so the card top is never above the gap and never pushed past the
    // bottom (when the card fits). max(G, ...) guards huge cards / tiny screens.
    top = Math.min(Math.max(top, G), Math.max(G, vh - G - cardH))
    const left = Math.min(Math.max(G, rect.left), Math.max(G, vw - W - G))
    return { top, left }
  }

  return {
    top: Math.max(G, (vh - cardH) / 2),
    left: Math.max(G, (vw - W) / 2),
  }
}
